import dayjs from "dayjs"
import { Truck } from "iconsax-reactjs"
import { useEffect, useState } from "react"

import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet"
import {
  translateDescription,
  translateDescriptionAsync,
  translateTitle,
  translateTitleAsync
} from "@/features/jobs/manage/constants/order-tracking.utils"
import { cn } from "@/modules/helpers/hooks/shared/utils"
import type { OrderLogistics, SubmissionOrder } from "@/modules/types/jobs/campaign"

// Sub-component for a single timeline item with async translation
const TimelineItem = ({ log, isFirst }: { log: OrderLogistics; isFirst: boolean }) => {
  const eventDate = dayjs(Number(log.eventTime))

  // Start with sync (instant) translation
  const [title, setTitle] = useState(() => translateTitle(log.title))
  const [description, setDescription] = useState(() => translateDescription(log.description))

  // Upgrade to async (API) translation if sync result still has English
  useEffect(() => {
    let cancelled = false
    translateTitleAsync(log.title).then((t) => {
      if (!cancelled) setTitle(t)
    })
    translateDescriptionAsync(log.description).then((d) => {
      if (!cancelled) setDescription(d)
    })
    return () => {
      cancelled = true
    }
  }, [log.title, log.description])

  return (
    <div key={log.id} className="flex gap-4">
      {/* Timeline dot and line */}
      <div className="flex flex-col items-center">
        <div
          className={cn(
            "w-3 h-3 rounded-full border-2 z-10",
            isFirst ? "bg-primary-800 border-primary-800" : "bg-white border-gray-300"
          )}
        />
        <div className="w-px flex-1 bg-gray-200 mt-1" />
      </div>

      {/* Content */}
      <div className="flex-1 pb-2">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1">
            <p
              className={cn("text-sm font-medium", isFirst ? "text-primary-800" : "text-gray-700")}
            >
              {title}
            </p>
            <p className="text-xs text-gray-500 mt-0.5 whitespace-pre-line">{description}</p>
          </div>
          <div className="text-right shrink-0">
            <p className="text-xs text-gray-500">{eventDate.format("HH:mm")}</p>
            <p className="text-xs text-gray-400">{eventDate.format("DD/MM")}</p>
          </div>
        </div>
      </div>
    </div>
  )
}

interface OrderTrackingSheetProps {
  open: boolean
  onClose: () => void
  order?: SubmissionOrder
}

export const OrderTrackingSheet = ({ open, onClose, order }: OrderTrackingSheetProps) => {
  if (!order) return null

  // Sort logistics by eventTime (newest first for display)
  const sortedLogistics = [...(order.ordersLogistics || [])].sort(
    (a, b) => Number(b.eventTime) - Number(a.eventTime)
  )

  return (
    <Sheet open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <SheetContent className="w-full h-[85vh] rounded-t-3xl p-4" side="bottom">
        <SheetTitle className="text-lg flex items-center gap-2 mb-4">
          <Truck size={20} className="text-primary-800" />
          Theo dõi đơn hàng
        </SheetTitle>
        <SheetDescription className="sr-only">Chi tiết hành trình đơn hàng</SheetDescription>
        <div className="overflow-y-auto h-[calc(100%-60px)]">
          {/* Order Summary Card */}
          <div className="space-y-3 bg-linear-to-r from-primary-50 to-primary-100 rounded-xl p-4 mb-6">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Mã đơn hàng</span>
              <span className="text-sm font-medium text-primary-800">
                #{order.orderId.slice(-8)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Đơn vị vận chuyển</span>
              <span className="text-sm font-medium">{order.deliveryService}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Số lượng</span>
              <span className="text-sm font-medium">{order.quantity}</span>
            </div>
            {order.trackingNumber && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Mã vận đơn</span>
                <span className="text-sm font-medium">{order.trackingNumber}</span>
              </div>
            )}
          </div>

          {/* Timeline */}
          <div>
            <h3 className="text-sm font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Truck size={16} className="text-primary-800" />
              Hành trình đơn hàng
            </h3>

            <div className="relative space-y-4">
              {sortedLogistics.map((log, index) => (
                <TimelineItem key={log.id} log={log} isFirst={index === 0} />
              ))}

              {sortedLogistics.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <Truck size={40} className="mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Chưa có thông tin vận chuyển</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  )
}
